import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../components/ui/alert-dialog";
import {
  Users,
  LogOut,
  Eye,
  CreditCard,
  Building2,
  FileText,
  DollarSign,
  MapPin,
  Hash,
  Save,
  X,
  CheckCircle2,
  Clock,
  AlertCircle,
  User,
  Phone,
  Mail,
  KeyRound,
  UserPlus,
  Trash2
} from "lucide-react";

const LOGO_URL = "/logo.png";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  const [planForm, setPlanForm] = useState({
    mailing_address: "",
    account_number: "",
    routing_number: "",
    available_credit_limit: "",
    notes: ""
  });

  // Password reset states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  // Create user states
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    role: "customer"
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Delete user states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, { headers: authHeaders });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const viewCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, { headers: authHeaders });
      setCustomerDetails(response.data);
      setSelectedCustomer(customerId);
      
      // Pre-fill plan form if plan exists
      if (response.data.plan) {
        setPlanForm({
          mailing_address: response.data.plan.mailing_address || "",
          account_number: response.data.plan.account_number || "",
          routing_number: response.data.plan.routing_number || "",
          available_credit_limit: response.data.plan.available_credit_limit?.toString() || "",
          notes: response.data.plan.notes || ""
        });
      } else {
        setPlanForm({
          mailing_address: "",
          account_number: "",
          routing_number: "",
          available_credit_limit: "",
          notes: ""
        });
      }
      
      setShowDetailsModal(true);
    } catch (error) {
      toast.error("Failed to fetch customer details");
    }
  };

  const savePlan = async () => {
    if (!planForm.mailing_address || !planForm.account_number || !planForm.routing_number || !planForm.available_credit_limit) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSavingPlan(true);
    try {
      await axios.post(
        `${API}/admin/customers/${selectedCustomer}/plan`,
        {
          ...planForm,
          available_credit_limit: parseFloat(planForm.available_credit_limit)
        },
        { headers: authHeaders }
      );
      toast.success("Plan saved successfully!");
      viewCustomerDetails(selectedCustomer); // Refresh
    } catch (error) {
      toast.error("Failed to save plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const updatePlanStatus = async (status) => {
    try {
      await axios.patch(
        `${API}/admin/customers/${selectedCustomer}/plan/status?status=${status}`,
        {},
        { headers: authHeaders }
      );
      toast.success(`Plan status updated to ${status}`);
      viewCustomerDetails(selectedCustomer); // Refresh
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/admin/reset-password`,
        { user_id: resetPasswordUser.id, new_password: newPassword },
        { headers: authHeaders }
      );
      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setShowResetPasswordModal(false);
      setNewPassword("");
      setResetPasswordUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.full_name || !createUserForm.email || !createUserForm.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (createUserForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setCreatingUser(true);
    try {
      await axios.post(
        `${API}/admin/create-user?role=${createUserForm.role}`,
        {
          full_name: createUserForm.full_name,
          email: createUserForm.email,
          phone: createUserForm.phone || null,
          password: createUserForm.password
        },
        { headers: authHeaders }
      );
      toast.success(`${createUserForm.role === 'admin' ? 'Admin' : 'Customer'} account created!`);
      setShowCreateUserModal(false);
      setCreateUserForm({ full_name: "", email: "", phone: "", password: "", role: "customer" });
      fetchCustomers(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const openResetPasswordModal = (customer) => {
    setResetPasswordUser(customer);
    setNewPassword("");
    setShowResetPasswordModal(true);
  };

  const openDeleteConfirm = (customer) => {
    setUserToDelete(customer);
    setShowDeleteConfirm(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}`,
        { headers: authHeaders }
      );
      toast.success(`User ${userToDelete.email} deleted`);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchCustomers(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUser(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, className: "status-pending" },
      active: { icon: CheckCircle2, className: "status-active" },
      completed: { icon: CheckCircle2, className: "status-completed" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.className}`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Credit2Cash" className="h-14 logo-glow" data-testid="admin-logo" />
            <span className="px-3 py-1 bg-[#FFD700]/20 text-[#FFD700] rounded-full text-sm font-semibold">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setShowCreateUserModal(true)}
              className="bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
              data-testid="create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
            <div className="hidden md:flex items-center gap-2 text-[#A1A1AA]">
              <User className="w-4 h-4" />
              <span>{user?.full_name}</span>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card" data-testid="total-customers-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Total Customers</p>
            <p className="text-4xl font-bold text-[#00FF66]">{customers.length}</p>
          </div>
          <div className="stat-card" data-testid="verified-customers-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Verified</p>
            <p className="text-4xl font-bold text-[#00F0FF]">
              {customers.filter(c => c.is_verified).length}
            </p>
          </div>
          <div className="stat-card" data-testid="pending-customers-stat">
            <p className="text-sm text-[#71717A] font-mono uppercase tracking-wider mb-2">Pending Verification</p>
            <p className="text-4xl font-bold text-[#FFD700]">
              {customers.filter(c => !c.is_verified).length}
            </p>
          </div>
        </div>

        {/* Customers Table */}
        <div className="premium-card rounded-2xl overflow-hidden" data-testid="customers-table">
          <div className="p-6 border-b border-white/5">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00FF66]" />
              Customer Accounts
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="table-dark w-full">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-[#71717A]">
                      No customers registered yet
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00FF66] to-[#00F0FF] flex items-center justify-center text-black font-bold">
                            {customer.full_name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-white font-medium">{customer.full_name}</span>
                        </div>
                      </td>
                      <td className="text-[#A1A1AA]">{customer.email}</td>
                      <td className="text-[#A1A1AA]">{customer.phone || "-"}</td>
                      <td>
                        {customer.is_verified ? (
                          <span className="inline-flex items-center gap-1 text-[#00FF66] text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#FFD700] text-sm">
                            <AlertCircle className="w-4 h-4" />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="text-[#71717A] font-mono text-sm">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => viewCustomerDetails(customer.id)}
                            variant="outline"
                            size="sm"
                            className="border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10"
                            data-testid={`view-customer-${customer.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            onClick={() => openResetPasswordModal(customer)}
                            variant="outline"
                            size="sm"
                            className="border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10"
                            data-testid={`reset-password-${customer.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => openDeleteConfirm(customer)}
                            variant="outline"
                            size="sm"
                            className="border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F]/10"
                            data-testid={`delete-user-${customer.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Customer Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#111] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00FF66] to-[#00F0FF] flex items-center justify-center text-black font-bold text-xl">
                {customerDetails?.user?.full_name?.charAt(0).toUpperCase()}
              </div>
              {customerDetails?.user?.full_name}
            </DialogTitle>
          </DialogHeader>

          {customerDetails && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="bg-black/30 rounded-xl p-4">
                <h3 className="text-sm font-mono text-[#71717A] uppercase tracking-wider mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#00FF66]" />
                    <span className="text-[#A1A1AA]">{customerDetails.user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-[#A1A1AA]">{customerDetails.user.phone || "Not provided"}</span>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/30 rounded-xl p-4">
                  <p className="text-sm text-[#71717A] mb-1">Total Debt</p>
                  <p className="text-2xl font-bold money-value">${customerDetails.total_debt?.toLocaleString()}</p>
                </div>
                <div className="bg-black/30 rounded-xl p-4">
                  <p className="text-sm text-[#71717A] mb-1">Total Credit Limit</p>
                  <p className="text-2xl font-bold text-[#00F0FF]">${customerDetails.total_credit_limit?.toLocaleString()}</p>
                </div>
              </div>

              {/* Credit Cards */}
              <div className="bg-black/30 rounded-xl p-4">
                <h3 className="text-sm font-mono text-[#71717A] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Credit Cards ({customerDetails.credit_cards?.length || 0})
                </h3>
                {customerDetails.credit_cards?.length === 0 ? (
                  <p className="text-[#71717A] text-sm">No credit cards added</p>
                ) : (
                  <div className="space-y-2">
                    {customerDetails.credit_cards.map((card, i) => (
                      <div key={i} className="flex justify-between items-center text-sm bg-black/20 rounded-lg p-3">
                        <div>
                          <span className="text-white font-medium">{card.card_type}</span>
                          <span className="text-[#71717A] ml-2">• {card.card_issuer}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-[#FF007F]">${card.balance?.toLocaleString()}</span>
                          <span className="text-[#71717A] mx-2">/</span>
                          <span className="text-[#00F0FF]">${card.credit_limit?.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Creditors */}
              <div className="bg-black/30 rounded-xl p-4">
                <h3 className="text-sm font-mono text-[#71717A] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Other Creditors ({customerDetails.creditors?.length || 0})
                </h3>
                {customerDetails.creditors?.length === 0 ? (
                  <p className="text-[#71717A] text-sm">No creditors added</p>
                ) : (
                  <div className="space-y-2">
                    {customerDetails.creditors.map((creditor, i) => (
                      <div key={i} className="flex justify-between items-center text-sm bg-black/20 rounded-lg p-3">
                        <div>
                          <span className="text-white font-medium">{creditor.creditor_name}</span>
                          {creditor.creditor_type && (
                            <span className="text-xs ml-2 px-2 py-0.5 bg-white/10 rounded text-[#A1A1AA]">
                              {creditor.creditor_type}
                            </span>
                          )}
                        </div>
                        <span className="text-[#FF007F]">${creditor.amount_owed?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Credit Reports */}
              <div className="bg-black/30 rounded-xl p-4">
                <h3 className="text-sm font-mono text-[#71717A] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Credit Reports ({customerDetails.credit_reports?.length || 0})
                </h3>
                {customerDetails.credit_reports?.length === 0 ? (
                  <p className="text-[#71717A] text-sm">No credit reports uploaded</p>
                ) : (
                  <div className="space-y-2">
                    {customerDetails.credit_reports.map((report, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm bg-black/20 rounded-lg p-3">
                        <FileText className="w-4 h-4 text-[#FF007F]" />
                        <span className="text-white">{report.filename}</span>
                        <span className="text-[#71717A] ml-auto">
                          {new Date(report.uploaded_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Plan Section */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Customer Plan</h3>
                  {customerDetails.plan && (
                    <div className="flex items-center gap-2">
                      {getStatusBadge(customerDetails.plan.status)}
                      <Select 
                        value={customerDetails.plan.status} 
                        onValueChange={updatePlanStatus}
                      >
                        <SelectTrigger className="w-32 input-dark" data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#00F0FF]" />
                      Mailing Address *
                    </Label>
                    <Textarea
                      value={planForm.mailing_address}
                      onChange={(e) => setPlanForm({ ...planForm, mailing_address: e.target.value })}
                      placeholder="Enter mailing address for cards"
                      className="input-dark mt-1"
                      rows={3}
                      data-testid="plan-address-input"
                    />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white text-sm flex items-center gap-2">
                        <Hash className="w-4 h-4 text-[#FFD700]" />
                        Account Number *
                      </Label>
                      <Input
                        value={planForm.account_number}
                        onChange={(e) => setPlanForm({ ...planForm, account_number: e.target.value })}
                        placeholder="Account number"
                        className="input-dark mt-1"
                        data-testid="plan-account-input"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-sm flex items-center gap-2">
                        <Hash className="w-4 h-4 text-[#00FF66]" />
                        Routing Number *
                      </Label>
                      <Input
                        value={planForm.routing_number}
                        onChange={(e) => setPlanForm({ ...planForm, routing_number: e.target.value })}
                        placeholder="Routing number"
                        className="input-dark mt-1"
                        data-testid="plan-routing-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-white text-sm flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#00FF66]" />
                      Available Credit Limit *
                    </Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                      <Input
                        type="number"
                        step="0.01"
                        value={planForm.available_credit_limit}
                        onChange={(e) => setPlanForm({ ...planForm, available_credit_limit: e.target.value })}
                        placeholder="0.00"
                        className="pl-8 input-dark"
                        data-testid="plan-limit-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Notes (Optional)</Label>
                    <Input
                      value={planForm.notes}
                      onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })}
                      placeholder="Any additional notes"
                      className="input-dark mt-1"
                      data-testid="plan-notes-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowDetailsModal(false)}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Close
                  </Button>
                  <Button
                    onClick={savePlan}
                    disabled={savingPlan}
                    className="bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                    data-testid="save-plan-btn"
                  >
                    {savingPlan ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Plan
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="max-w-md bg-[#111] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#FFD700]" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {resetPasswordUser && (
              <div className="bg-black/30 rounded-xl p-4">
                <p className="text-sm text-[#71717A] mb-1">Resetting password for:</p>
                <p className="text-white font-semibold">{resetPasswordUser.full_name}</p>
                <p className="text-[#A1A1AA] text-sm">{resetPasswordUser.email}</p>
              </div>
            )}

            <div>
              <Label className="text-white text-sm">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 6 characters"
                className="input-dark mt-1"
                data-testid="admin-new-password-input"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowResetPasswordModal(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="bg-[#FFD700] text-black font-bold hover:bg-[#FFD700]/90"
                data-testid="confirm-reset-password-btn"
              >
                {resettingPassword ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Resetting...
                  </span>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="max-w-md bg-[#111] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#00FF66]" />
              Create New User
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white text-sm">Full Name *</Label>
              <Input
                value={createUserForm.full_name}
                onChange={(e) => setCreateUserForm({ ...createUserForm, full_name: e.target.value })}
                placeholder="John Doe"
                className="input-dark mt-1"
                data-testid="create-user-name"
              />
            </div>

            <div>
              <Label className="text-white text-sm">Email *</Label>
              <Input
                type="email"
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                placeholder="user@example.com"
                className="input-dark mt-1"
                data-testid="create-user-email"
              />
            </div>

            <div>
              <Label className="text-white text-sm">Phone (Optional)</Label>
              <Input
                value={createUserForm.phone}
                onChange={(e) => setCreateUserForm({ ...createUserForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="input-dark mt-1"
                data-testid="create-user-phone"
              />
            </div>

            <div>
              <Label className="text-white text-sm">Password *</Label>
              <Input
                type="password"
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                placeholder="Min. 6 characters"
                className="input-dark mt-1"
                data-testid="create-user-password"
              />
            </div>

            <div>
              <Label className="text-white text-sm">Role</Label>
              <Select
                value={createUserForm.role}
                onValueChange={(v) => setCreateUserForm({ ...createUserForm, role: v })}
              >
                <SelectTrigger className="input-dark mt-1" data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateUserModal(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={creatingUser}
                className="bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                data-testid="confirm-create-user-btn"
              >
                {creatingUser ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Creating...
                  </span>
                ) : (
                  "Create User"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-[#111] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-[#FF007F]" />
              Delete User
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to delete <span className="text-white font-semibold">{userToDelete?.full_name}</span> ({userToDelete?.email})?
              <br /><br />
              <span className="text-[#FF007F]">This will permanently delete all their data including credit cards, creditors, credit reports, and plans. This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deletingUser}
              className="bg-[#FF007F] text-white hover:bg-[#FF007F]/80"
              data-testid="confirm-delete-user-btn"
            >
              {deletingUser ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete User"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
